import { trigger } from "@angular/animations";
import { HttpClient, HttpErrorResponse } from "@angular/common/http";
import { Injectable, OnDestroy } from "@angular/core";
import { Store } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import { Mutex } from "async-mutex";
import moment from "moment";
import { IMqttMessage } from "ngx-mqtt";
import { Observable, Subscription, throwError } from "rxjs";
import { catchError, debounceTime, filter, map, tap } from "rxjs/operators";
import { environment } from "src/environments/environment";
import { AppReserved } from "src/model/class/general classes/app-reserved";
import { AppSettings } from "src/components/shared-components/class/general-classes/app-settings";
import {
  Card,
  CardSimple,
  CardUpdate,
} from "src/model/class/general classes/card";
import { CardEnvironment } from "src/model/class/general classes/card-environment";
import { SaltoKSTag } from "src/model/class/general classes/saltoKS-tag";
import { StaticClass } from "src/components/shared-components/class/general-classes/static-classes";
import { Settings } from "src/model/class/settings";
import {
  CardEffectiveState,
  CardKind,
  CardState,
} from "src/components/shared-components/enum/enumCard";
import { CustomerStaffType } from "src/components/shared-components/enum/enumCustomerStaff";
import { EnvironmentType } from "src/components/shared-components/enum/enumEnvironment";
import { GenericOperationKind } from "src/model/enum/enumGenericOperationKind";
import { LogLevel } from "src/components/shared-components/enum/enumLog";
import { ManufacturerSystem, ToastType } from "src/components/shared-components/enum/enumMisc";
import {
  CardFastCheckin,
  CardFastCheckinResponse,
} from "src/model/interface/card-fast-checkin";
import { EnvironmentAccessControlMaintanance } from "src/model/interface/environment-access-control-maintanance";
import { GenericStoreApiData } from "src/components/shared-components/interfaces/generic-store-api-data";
import { iEvent } from "src/components/shared-components/interfaces/iEvent";
import { iMqttHandling } from "src/model/interface/iMqtt-handling";
import {
  cardEnvironmentAssignCalendarAction,
  cardEnvironmentAssignTimeSlotAction,
  cardEnvironmentSwithcActiveSuccessAction,
} from "src/state/actions/card-environment.actions";
import { listCardEnvironment } from "src/state/selectors/card-environment.selectors";
import { listEnvironmentAccessControlMaintanance } from "src/state/selectors/environment.selectors";
import { serverSettings } from "src/state/selectors/server-settings-selector";
import { AlertService } from "./alert.service";
import { CustomerStaffService } from "./customer-staff.service";
import { EnvironmentService } from "./environment.service";
import { EventService } from "./event.service";
import { LoaderService } from "./loader.service";
import { LoggerService } from "./logger.service";
import { MqttClientService } from "./mqtt-client.service";
import { ToastService } from "./toast.service";
import { MomentFormats } from "src/model/class/general classes/moment-formats";

@Injectable({
  providedIn: "root",
})
export class CardsService implements OnDestroy, iEvent, iMqttHandling {
  private _selectServerSettingsSubscription: Subscription = null;
  private listEnvironmentAccessControlMaintanance: Array<EnvironmentAccessControlMaintanance> =
    new Array<EnvironmentAccessControlMaintanance>();

  private _logContext: string = "Cards.service";
  private _locked: string = "";
  private _new: string = "";
  private _active: string = "";
  private _expired: string = "";
  private _awaitingActivation: string = "";
  private _serverSettings: Settings = null;
  private _lblUIDMandatory: string = "";
  private _lblAnotherCardWithSameUID: string = "";
  private _lblNeedToDeleteCard: string = "";
  private _lblUID8Char: string = "";
  private _lblEnvironmentMandatory: string = "";
  private _mutex = new Mutex();
  private listCardEnvironment: Array<CardEnvironment> = [];
  private _listEnvironmentAccessControlSubscription: Subscription = null;
  private _listCardEnvironmentSubscription: Subscription = null;
  private _translateSubscription: Subscription = null;

  _eventSubscriptions: Array<Subscription> = null;
  _mqttAddressSubscriptionsMap: Map<string, Subscription> = null;

  constructor(
    private _http: HttpClient,
    private _loggerService: LoggerService,
    private _store: Store,
    private _mqttClient: MqttClientService,
    private _eventService: EventService,
    private _mqttService: MqttClientService,
    private _enviromentService: EnvironmentService,
    private _loaderService: LoaderService,
    private _translateService: TranslateService,
    private _alertService: AlertService,
    private _toastService: ToastService,
    private _customerStaffService: CustomerStaffService
  ) {
    this._mqttAddressSubscriptionsMap = new Map<string, Subscription>();
    this.loadServerSettings();

    this.subscribeMQTTAddresses();
    this.loadListCardEnvironment();
    this.loadListEnvironmentAccessControl();
    this.loadTranslation();
    this.subscribeTranslationEvent();
  }

  ngOnDestroy(): void {
    this.unsubscribeStoreSelect();

    this.unsubscribeToMQTTAddresses();
    this._listCardEnvironmentSubscription?.unsubscribe();
    this._listEnvironmentAccessControlSubscription?.unsubscribe();
    this._translateSubscription?.unsubscribe();
  }

  //#region Subscribe to event-service observable
  subscribeEvents(): void {
    this._eventSubscriptions = new Array<Subscription>();

    this._eventSubscriptions.push(
      this._eventService.OnLoginPlantDid.subscribe(() => {
        this.subscribeMQTTAddresses();
      })
    );

    /** Operation performed on logout */
    this._eventSubscriptions.push(
      this._eventService.OnMakeLogout.subscribe(async () => {
        await this.unsubscribeToMQTTAddresses();
      })
    );
  }

  unsubscribeEvents(): void {
    for (let sub of this._eventSubscriptions) {
      sub.unsubscribe();
    }
  }
  //#endregion

  //#region Mqtt addresses handling

  /**
   * Subscribtion from address service
   */
  async subscribeMQTTAddresses(): Promise<void> {
    let mqttAddressSubcription: Subscription = null;
    let returnedObservable: Observable<IMqttMessage> = null;

    //#region Subscribing to RESERVED_DB_CARD_DATAUPDATED
    const mqttAddressCardUpdated: string =
      AppSettings.INSTANCE_GUID_VIRTUAL_VARIABLE +
      "_" +
      AppReserved.RESERVED_DB_CARD_DATAUPDATED;

    returnedObservable = await this._mqttService.subscribeTopic(
      mqttAddressCardUpdated
    );

    mqttAddressSubcription = returnedObservable
      .pipe(
        map((mqttMessage: IMqttMessage) => {
          return this._mqttService.parseMessage(mqttMessage);
        }),
        filter(
          (message: Array<string>) =>
            (message?.[2] || "") !== this._mqttService.clientID
        ),
        map(async (message: Array<string>) => {
          if (message.length === 3) {
            const release = await this._mutex.acquire();

            try {
              //return message[1];
              return message;
            } finally {
              release();
            }
          } else {
            throw new Error("OnCardChanged - Incorrect format message");
          }
        }),
        debounceTime(1000)
      )
      .subscribe(
        (message) => {
          message.then(async (message) => {
            const operatiopnKind = message[0];
            const idCardChanged = message[1];

            this._loggerService.log(
              LogLevel.LOG_INFO,
              this._logContext,
              "_mqttService.subscribeTopic",
              "AppReserved.RESERVED_DB_CARD_DATAUPDATED",
              "NEW idCustomerStaffChanged: " + idCardChanged
            );

            const updatedCard: Card = await this.getCardByIDCard(
              Number(idCardChanged)
            );

            switch (+operatiopnKind) {
              case GenericOperationKind.CARD_REMOVE:
                this._eventService.OnCardRemoved.next(updatedCard);

                break;

              //ALL OTHER Operation kinds on cards
              default:
                this._eventService.OnCardChanged.next(updatedCard);
                break;
            }
          });
        },
        (error) => {
          this._loggerService.log(
            LogLevel.LOG_ERROR,
            this._logContext,
            "_mqttService.subscribeTopic",
            "AppReserved.RESERVED_DB_CARD_DATAUPDATED",
            "Incorect message format: " + error.message
          );
        }
      );

    await this.checkLocalAddressSubscriptionMap(
      mqttAddressCardUpdated,
      mqttAddressSubcription
    );

    //#endregion

    //#region Subscribing to RESERVED_DB_CARDENVIRONMENT_DATAUPDATED
    const mqttAddressCardEnvironmentUpdated: string =
      AppSettings.INSTANCE_GUID_VIRTUAL_VARIABLE +
      "_" +
      AppReserved.RESERVED_DB_CARDENVIRONMENT_DATAUPDATED;

    returnedObservable = await this._mqttService.subscribeTopic(
      mqttAddressCardEnvironmentUpdated
    );

    mqttAddressSubcription = returnedObservable
      .pipe(
        map((mqttMessage: IMqttMessage) => {
          return this._mqttService.parseMessage(mqttMessage);
        }),
        filter(
          (message: Array<string>) =>
            (message?.[2] || "") !== this._mqttService.clientID
        ),
        map(async (message: Array<string>) => {
          if (message.length === 4) {
            const release = await this._mutex.acquire();

            try {
              const updatedCard: Card = await this.getCardByIDCard(
                Number(message[1])
              );

              if (updatedCard) {
                this._eventService.OnCardChanged.next(updatedCard);
              }

              return message[2];
            } finally {
              release();
            }
          } else {
            throw new Error(
              "OnCardChangedEnvironment - Incorrect format message"
            );
          }
        }),
        debounceTime(1000)
      )
      .subscribe(
        (promiseCardEnvironmentChanged) => {
          promiseCardEnvironmentChanged.then(
            async (idCardEnvironmentChanged: string) => {
              this._loggerService.log(
                LogLevel.LOG_INFO,
                this._logContext,
                "_mqttService.subscribeTopic",
                "AppReserved.RESERVED_DB_CARDENVIRONMENT_DATAUPDATED",
                "NEW idCustomerStaffChanged: " + idCardEnvironmentChanged
              );

              const updatedCardEnvironment: CardEnvironment =
                await this.getCardEnvironmentsByID(
                  Number(idCardEnvironmentChanged)
                );

              if (updatedCardEnvironment) {
                this._eventService.OnCardEnvironmentChanged.next(
                  updatedCardEnvironment
                );
              }
            }
          );
        },
        (error) => {
          this._loggerService.log(
            LogLevel.LOG_ERROR,
            this._logContext,
            "_mqttService.subscribeTopic",
            "AppReserved.RESERVED_DB_CARDENVIRONMENT_DATAUPDATED",
            "Incorect message format: " + error.message
          );
        }
      );

    await this.checkLocalAddressSubscriptionMap(
      mqttAddressCardEnvironmentUpdated,
      mqttAddressSubcription
    );

    //#endregion
  }

  async checkLocalAddressSubscriptionMap(
    addressToCheck: string,
    addressSubscription: Subscription
  ): Promise<boolean> {
    let result: boolean = false;
    if (this._mqttAddressSubscriptionsMap.has(addressToCheck)) {
      addressSubscription.unsubscribe();

      this._loggerService.log(
        LogLevel.LOG_INFO,
        this._logContext,
        this._logContext,
        "checkLocalAddressSubscriptionMap()",
        "Address ALREADY present " + addressToCheck
      );
    } else {
      this._mqttAddressSubscriptionsMap.set(
        addressToCheck,
        addressSubscription
      );

      this._loggerService.log(
        LogLevel.LOG_INFO,
        this._logContext,
        this._logContext,
        "checkLocalAddressSubscriptionMap()",
        "NEW address: " + addressToCheck
      );
      result = true;
    }
    return result;
  }

  async unsubscribeToMQTTAddresses(): Promise<void> {
    for (const entry of Array.from(
      this._mqttAddressSubscriptionsMap.entries()
    )) {
      entry[1].unsubscribe();

      this._mqttAddressSubscriptionsMap.delete(entry[0]);
    }
  }

  async unsubscribeToSingleMQTTAddress(address: string): Promise<void> {
    this._loggerService.log(
      LogLevel.LOG_INFO,
      this._logContext,
      "_eventService",
      "unsubscribeToSingleMQTTAddress",
      "LOCAL MAP-SUBSCRIPTIONS 1: "
    );
    for (const entry of Array.from(
      this._mqttAddressSubscriptionsMap.entries()
    )) {
      this._loggerService.log(
        LogLevel.LOG_INFO,
        this._logContext,
        "_eventService",
        "unsubscribeToSingleMQTTAddress",
        "LOCAL MAP-SUBSCRIPTIONS 1 - ENTRY: " + entry[0]
      );
    }

    if (this._mqttAddressSubscriptionsMap.has(address)) {
      const subToObservable = this._mqttAddressSubscriptionsMap.get(address);
      subToObservable.unsubscribe();
      this._mqttAddressSubscriptionsMap.delete(address);
    }

    for (const entry of Array.from(
      this._mqttAddressSubscriptionsMap.entries()
    )) {
      this._loggerService.log(
        LogLevel.LOG_INFO,
        this._logContext,
        "_eventService",
        "unsubscribeToSingleMQTTAddress",
        "LOCAL MAP-SUBSCRIPTIONS 2 - ENTRY: " + entry[0]
      );
    }
  }

  //#endregion

  //#region API Call
  /**
   * Get  list of cards, filtred by a peculiar queryString
   *
   * @param queryString Query string for detailed search
   * @returns
   */
  public async getCardsByQueryString(
    queryString: string = ""
  ): Promise<Array<Card>> {
    if (queryString.length > 0) {
      queryString = "?" + queryString;
    }

    return this._http
      .get<Array<Card>>(
        environment.httpServerRoot + AppSettings.API_URL_CARD + queryString
      )
      .toPromise();
  }

  /**Get complete list of "valid" cards from server */
  public async getValidCards(): Promise<Array<Card>> {
    return await this.getCardsByQueryString("valid=1");
  }

  /**Get card by card number  */
  public async getCardByCardNumber(cardNumber: number): Promise<Array<Card>> {
    return await this.getCardsByQueryString("cardNumber=" + cardNumber);
  }

  /**Get card by UID  */
  public async getCardByUID(UID: String): Promise<Array<Card>> {
    return await this.getCardsByQueryString("cardUID=" + UID);
  }

  /**Get complete list of cards from server */
  public async getAllCards(): Promise<Array<Card>> {
    return await this.getCardsByQueryString("");
  }

  /**
   * Get single card by id
   *
   * @param idCard ID card to get data
   * @returns
   */
  public async getCardByIDCard(idCard: number): Promise<Card> {
    try {
      const stringQuery: string = "idCard=" + idCard;
      const listCard: Array<Card> = await this.getCardsByQueryString(
        stringQuery
      );

      if (listCard) {
        return listCard?.[0];
      } else {
        throw new Error();
      }
    } catch {
      throw new Error();
    }
  }

  /**
   * Get list of card, associated with a peucliar Cutomer/Staff member
   * @param idCustomerStaff ID of peculiar customer/staff
   * @param includeOnlyValidCards Flag value; define if search should be restricted only to valid cards (DEFAULT: false)
   * @returns
   */
  public async getCardsByIDCustomerStaff(
    idCustomerStaff: number,
    includeOnlyValidCards: boolean = false
  ): Promise<Array<Card>> {
    let stringQuery: string = "idCustomerStaff=" + idCustomerStaff;

    if (includeOnlyValidCards) {
      stringQuery = stringQuery + "&valid=1";
    }
    return await this.getCardsByQueryString(stringQuery);
  }

  /**
   * Get list card by id device
   *
   * @param idDevice ID device to take card
   * @param includeOnlyActive Include only valid card
   * @returns
   */
  public getCardsByIDDevice(
    idDevice: number,
    includeOnlyActive: boolean = true
  ): Observable<Array<Card>> {
    let queryString: string = "";

    if (includeOnlyActive) {
      queryString += "?includeOnlyActive=" + true;
    }

    return this._http.get<Array<Card>>(
      environment.httpServerRoot +
      AppSettings.API_URL_CARD +
      "/device/" +
      idDevice +
      queryString
    );
  }

  /**
   * Get cards template from API.
   * If api call fallback in error costructor, it propagate an Observable notification with .error() function
   */
  public async getCardTemplate(): Promise<Array<Card>> {
    const queryString: string = "?isTemplate=1";

    return this._http
      .get<Array<Card>>(
        environment.httpServerRoot + AppSettings.API_URL_CARD + queryString
      )
      .toPromise();
  }

  /**
   * Get all Salto KS Tags by query
   * @param query query string
   * @returns SaltoKSTag object list
   */
  public async getSaltoTags(query: string): Promise<SaltoKSTag[]> {
    return this._http
      .get<Array<SaltoKSTag>>(
        environment.httpServerRoot + AppSettings.API_URL_CARD
        + "/saltokstag?" + query
      ).toPromise();
  }

  /**
   *  Create new card
   *
   *  @param cardSimple Card data for creation
   */
  public async createCard(cardSimple: CardSimple): Promise<Card> {
    return this._http
      .post<Card>(
        environment.httpServerRoot + AppSettings.API_URL_CARD,
        cardSimple
      )
      .pipe(
        tap((createdCard: Card) => {
          this.propagateCardOperation(
            GenericOperationKind.CARD_INSERT,
            createdCard.id
          );
        }),
        tap((createdCard: Card) => {
          this._eventService.OnCardCreated.next(createdCard);
        })
      )
      .toPromise();
  }

  /**
   * Update time slot on card environment
   * @param idCardEnvironment
   * @param idTimeSlot
   */
  public async updateTimeSlotOnCardEnvironment(
    idCardEnvironment: number,
    idTimeSlot: number
  ) {
    const bodyRequest = {
      idTimeslot: idTimeSlot,
    };

    return this._http
      .patch(
        environment.httpServerRoot +
        AppSettings.API_URL_CARD +
        "/environment/" +
        idCardEnvironment +
        "/timeslot1",
        bodyRequest
      )
      .toPromise();
  }

  /**
   *  Update card via API
   *
   *  @param cardUpdate card to update
   *  @param idCard id card to update
   */
  public async updateCard(cardUpdate: CardUpdate, idCard: number) {
    return this._http
      .put<Card>(
        environment.httpServerRoot + AppSettings.API_URL_CARD + "/" + idCard,
        cardUpdate
      )
      .pipe(
        tap((updatedCard: Card) => {
          this.propagateCardOperation(GenericOperationKind.CARD_EDIT, idCard);
        })
      )
      .toPromise();
  }

  /**
   *  Delete existing customer
   * @param idDeleteCard delete existing card
   */
  public async deleteCard(idDeleteCard: number) {
    return this._http
      .delete(
        environment.httpServerRoot +
        AppSettings.API_URL_CARD +
        "/" +
        idDeleteCard
      )
      .pipe(
        tap(() => {
          this.propagateCardOperation(
            GenericOperationKind.CARD_REMOVE,
            idDeleteCard
          );
        }),
        catchError((error: HttpErrorResponse) => {
          return throwError(error);
        })
      )
      .toPromise();
  }

  /**
   *  Lock existing card
   *  @param idCard ID card to lock
   */
  public async lockCard(idCard: number) {
    return this._http
      .post(
        environment.httpServerRoot +
        AppSettings.API_URL_CARD +
        "/" +
        idCard +
        "/lock",
        idCard
      )
      .pipe(
        tap((blockCard: Card) => {
          this.propagateCardOperation(GenericOperationKind.CARD_BLOCK, idCard);
        }),
        catchError((error: HttpErrorResponse) => {
          return throwError(error);
        })
      )
      .toPromise();
  }

  /**
   *  Activate existing card //
   *
   *  @param idCard id card to activate 
   */
  public async activateCard(idCard: number) {
    return this._http
      .post(
        environment.httpServerRoot +
        AppSettings.API_URL_CARD +
        "/" +
        idCard +
        "/activate",
        idCard
      )
      .pipe(//
        tap((activatedCard: Card) => {
          this.propagateCardOperation(
            GenericOperationKind.CARD_ACTIVATE,
            idCard
          );
        }),
        catchError((error: HttpErrorResponse) => {
          return throwError(error);
        })
      )
      .toPromise();
  }

  /**
   * Activate/Deactivate Salto KS card
   * @param idCard id card to activate
   * @param idSaltoTag id Salto tag to assign to card
   * @param activate used to propagate info
   */
  public enableDisableSaltoCard(idCard: number, idSaltoTag: number, activate: boolean): Promise<object> {
    const body = {
      idSaltoTag: idSaltoTag,
      enable: activate
    };
    return this._http
      .patch(
        environment.httpServerRoot +
        AppSettings.API_URL_CARD +
        "/" + idCard + "/assign/saltokstag",
        body
      ).pipe(
        tap(() => {
          this.propagateCardOperation(
            activate ? GenericOperationKind.CARD_ACTIVATE : GenericOperationKind.CARD_BLOCK,
            idCard
          );
        }),
        catchError((error: HttpErrorResponse) => {
          return throwError(error);
        })
      )
      .toPromise();
  }

  /**
   * Clone existing card
   *
   * @param idCard ID card to clonse
   * @returns Caard cloned
   */
  public async cloneCard(idCard: number): Promise<Card> {
    return this._http
      .post<Card>(
        environment.httpServerRoot +
        AppSettings.API_URL_CARD +
        "/" +
        idCard +
        "/clone",
        {}
      )
      .pipe(
        tap((clonedCard: Card) => {
          this.propagateCardOperation(
            GenericOperationKind.CARD_CLONE,
            clonedCard.id
          );
        }),
        tap((clonedCard: Card) => {
          this._eventService.OnCardCreated.next(clonedCard);
        })
      )
      .toPromise();
  }

  /**
   * Send notification
   *
   * @param idCard
   * @returns
   */
  public async notifyCard(
    idCard: number,
    listNotification: Array<number>
  ): Promise<any> {
    const bodyRequest = {
      ids: listNotification,
    };
    return this._http
      .post(
        environment.httpServerRoot +
        AppSettings.API_URL_CARD +
        "/" +
        idCard +
        "/notify",
        bodyRequest
      )
      .toPromise()
      .then(
        (result: any) => {
          return result;
        },
        (error) => {
          throw new Error();
        }
      );
  }

  /**
   * Apply card template to specific card
   *
   * @param idCard ID card to applu template
   * @returns Returned promise
   */
  public async applyTemplate(idCard: number) {
    return this._http
      .post(
        environment.httpServerRoot +
        AppSettings.API_URL_CARD +
        "/" +
        idCard +
        "/apply/template",
        {}
      )
      .pipe(
        tap((applyTemplateCard: Card) => {
          this.propagateCardOperation(
            GenericOperationKind.CARD_TEMPLATE_CHANGE,
            applyTemplateCard.id
          );
        })
      )
      .toPromise();
  }

  /**
   * Create card with fast checkin operation
   *
   * @param cardFastCheckInData Fast checkin data
   */
  public async executeFastCheckIn(
    cardFastCheckInData: CardFastCheckin
  ): Promise<CardFastCheckinResponse> {
    return this._http
      .post<CardFastCheckinResponse>(
        environment.httpServerRoot + AppSettings.API_URL_CARD + "/fastcheckin",
        cardFastCheckInData
      )
      .pipe()
      .toPromise();
  }

  /**
   * Set UID on specific card
   * @param idCard
   * @param UID
   * @returns
   */
  public async setUIDOnCard(idCard: number, UID: string) {
    const bodyRequest = {
      uid: UID.toUpperCase(),
    };
    return this._http
      .patch(
        environment.httpServerRoot +
        AppSettings.API_URL_CARD +
        "/" +
        idCard +
        "/update/uid",
        bodyRequest
      )
      .pipe()
      .toPromise();
  }

  /**
   * Get available card kind by environment details
   *
   * @param environment Environment details
   * @returns
   */
  public getCardKindByEnvironment(
    environment: EnvironmentAccessControlMaintanance
  ): CardKind {
    let returCardKind: CardKind = CardKind.UNKNOWN;

    switch (environment.manufacturerSystem) {
      case ManufacturerSystem.VIMAR_EELECTRON:
      case ManufacturerSystem.VIMAR_WELL_CONTACT_PLUS:
      case ManufacturerSystem.SCHNEIDER:
      case ManufacturerSystem.SIEMENS:
      case ManufacturerSystem.EELECTRON:
      case ManufacturerSystem.GEWISS:
      case ManufacturerSystem.ABB_MAC2:
      case ManufacturerSystem.DORMAKABASAFLOKSYSTEM6000:
      case ManufacturerSystem.ABB:
      case ManufacturerSystem.SALTO_IS:
      case ManufacturerSystem.WEIGAND:
      case ManufacturerSystem.ABB_MILLENIUM:
      case ManufacturerSystem.ABB_TACTEO:
      case ManufacturerSystem.BLUMOTIX_5:
      case ManufacturerSystem.BLUMOTIX_10:
      case ManufacturerSystem.BIG_VIRTUAL_KEYPAD:
      case ManufacturerSystem.BIG_VIRTUAL_MIFARE:
      case ManufacturerSystem.GENERIC_KEYPAD:
      case ManufacturerSystem.DAHUA:
      case ManufacturerSystem.DAHUA_CARD_READER:
        returCardKind = CardKind.REAL_CARD;
        break;
      case ManufacturerSystem.BIG_VIRTUAL:
        if (
          (environment.qrScannerCount > 0 &&
            environment.numericKeypadCount > 0) ||
          (environment.qrScannerCount === 0 &&
            environment.numericKeypadCount === 0)
        ) {
          returCardKind = CardKind.REAL_CARD;
        } else if (environment.qrScannerCount > 0) {
          returCardKind = CardKind.QRCODE;
        } else if (environment.numericKeypadCount > 0) {
          returCardKind = CardKind.VIRTUAL_KEYPAD_CODE;
        }
        break;
      case ManufacturerSystem.MIKROTIK:
        returCardKind = CardKind.MAIL;
        break;
      case ManufacturerSystem.MULTI_MANUFACTURERSYSTEM:
        returCardKind = CardKind.PLATE;
        break;
      default:
        break;
    }

    return returCardKind;
  }

  /**
   * Get card expiration days by customer staff type
   *
   * @param customerStaffType Customer staff type
   * @returns Day card duration
   */
  public getCardExpirationDurationByCustomerStaffType(
    customerStaffType: CustomerStaffType
  ): number {
    let dayDuration: number = Number(
      this._serverSettings.DefaultCustomerCardsExpirationDays
    );

    if (customerStaffType === CustomerStaffType.STAFF) {
      dayDuration = Number(
        this._serverSettings.DefaultStaffCardsExpirationDays
      );
    }

    return dayDuration;
  }

  /**
   * Calculate card expiration time by server settings
   *
   * @returns Expiration time (es 23:59)
   */
  public getCardExpirationTimeBySystemSettings(): string {
    let expirationDate: string = "23:59";
    if (this._serverSettings.UseCheckOutTimeForCustomerCards === "TRUE") {
      expirationDate = this._serverSettings.DefaultDepartureTime;
      expirationDate = moment(
        this._serverSettings.DefaultDepartureTime,
        "HH:m"
      ).format("HH:mm");
    }

    return expirationDate;
  }

  /**
   * Calculate card start validity time by system settings
   *
   * @returns Start validity time (es. 00:00)
   */
  public getCardStartValidityTimeBySystemSettings(): string {
    let startValidity: string = "00:00";
    if (this._serverSettings.UseCheckInTimeForCustomerCards === "TRUE") {
      startValidity = this._serverSettings.DefaultArrivalTime;
      startValidity = moment(
        this._serverSettings.DefaultArrivalTime,
        "HH:m"
      ).format("HH:mm");
    }

    return startValidity;
  }

  public getCardNumber(currentCard: Card): string {
    let cardInfoNumber: string = "";
    if (
      currentCard.cardKind == CardKind.PLATE ||
      currentCard.cardKind == CardKind.MAIL ||
      currentCard.cardKind == CardKind.PHONE_NUMBER
    ) {
      cardInfoNumber = currentCard.cardCode.toString();
    } else {
      cardInfoNumber = currentCard.cardNumber.toString();
    }

    return cardInfoNumber;
  }

  /**
   * Check if selected card is expired; it does so confronting the unified expiration date and time
   * of card with current date/time.
   * @param selectedCard
   * @returns
   */
  public isCardIsExpired(selectedCard: Card): boolean {
    let result: boolean = false;

    const unifyDataTimeCardExpiration: string = StaticClass.unifyDateTime(
      selectedCard.endDay,
      selectedCard.endTime
    );

    result = moment().isAfter(
      moment(
        unifyDataTimeCardExpiration,
        MomentFormats.MOMENT_DATETIME_FORMAT_DEVICE_VALID_LIST
      )
    );

    return result;
  }

  /**
   * Get card kind icon class
   *
   * @param cardKind Card Kind to get icon
   * @returns
   */
  public getCardKindIconClass(cardKind: CardKind): string {
    let iconClass: string = "";

    switch (cardKind) {
      case CardKind.FACE:
        iconClass = "bigstudio3-face-recognition";
        break;
      case CardKind.MAIL:
        iconClass = "bigstudio3-email";
        break;
      case CardKind.PHONE_NUMBER:
        iconClass = "bigstudio3-phone";
        break;
      case CardKind.PLATE:
        iconClass = "bigstudio3-car-plate";
        break;
      case CardKind.QRCODE:
        iconClass = "bigstudio3-qrcode";
        break;
      case CardKind.VIRTUAL_KEYPAD_CODE:
        iconClass = "bigstudio3-keypad";
        break;
      default:
        iconClass = "bigstudio3-card-bigstudio";
        break;
    }

    return iconClass;
  }

  //#endregion

  /**
   * Show alert to insert UID and update cards
   */
  public async showPromptAndSetUID(cardSelected: Card) {
    let returnCard: Card = null;
    let saveUID: boolean = false;
    try {
      let uidReturned = await this._alertService.showAlertSetUID(
        cardSelected.cardUID
      );

      if (
        uidReturned &&
        uidReturned.trim().length == 8 &&
        this.hasAtLeastOneEnvironments()
      ) {
        let listCard: Array<Card> = [];
        listCard = await this.getCardByUID(uidReturned);
        if (listCard.length == 0) {
          saveUID = true;
        }
        if (listCard.length == 1 && listCard[0].id == cardSelected.id) {
          saveUID = true;
        }
        if (saveUID) {
          try {
            await this.setUIDOnCard(cardSelected.id, uidReturned);

            cardSelected.cardUID = uidReturned;
            if (cardSelected.state != CardState.REGISTERED) {
              cardSelected.state = CardState.REGISTERED;
            }
            returnCard = cardSelected;
          } catch {
            await this._alertService.presentAlertSomethingWrong();
          }
        } else {
          let cardFoundWithUID = listCard.find(
            (card) => card.id != cardSelected.id
          );
          let customerWithSameUID =
            await this._customerStaffService.getCustomerStaffbyID(
              cardFoundWithUID.idCustomer
            );
          this._toastService.showToast(
            ToastType.ERROR,
            this._lblAnotherCardWithSameUID +
            "  " +
            customerWithSameUID?.name +
            "  " +
            customerWithSameUID?.surname +
            this._lblNeedToDeleteCard,
            4000
          );
        }
      } else {
        if (uidReturned) {
          this._toastService.showToast(
            ToastType.ERROR,
            this._lblUID8Char,
            2000
          );
        } else if (uidReturned == "") {
          this._toastService.showToast(
            ToastType.ERROR,
            this._lblUIDMandatory,
            2000
          );
        } else {
          this._toastService.showToast(
            ToastType.ERROR,
            this._lblEnvironmentMandatory,
            2000
          );
        }
      }
    } catch {
      //prompt is closed
    }
    return returnCard;
  }

  /**
   * Get card environment by generic query string
   *
   * @param queryString
   * @returns
   */
  public getCardEnvironmentsByQueryString(
    queryString: string
  ): Observable<Array<CardEnvironment>> {
    if (queryString.length > 0) {
      queryString = "?" + queryString;
    }

    return this._http.get<Array<CardEnvironment>>(
      environment.httpServerRoot +
      AppSettings.API_URL_CARD_ENVIRONMENT +
      queryString
    );
  }

  /**
   * Get card environment by id
   *
   * @param idCardEnvironment ID card environment to get environment
   * @returns
   */
  public async getCardEnvironmentsByID(
    idCardEnvironment: number
  ): Promise<CardEnvironment> {
    const listCardEnvironment: Array<CardEnvironment> =
      await this.getCardEnvironmentsByQueryString(
        "id=" + idCardEnvironment
      ).toPromise();

    return listCardEnvironment?.[0] || null;
  }

  /**
   * Get card environment for specific cards
   *
   * @param idCard ID card to get releated card environment
   * @returns
   */
  public getCardEnvironmentsByIDCard(
    idCard: number
  ): Observable<Array<CardEnvironment>> {
    return this.getCardEnvironmentsByQueryString("idCard=" + idCard);
  }

  /**
   * Set passepartout on card
   * @param card
   */
  public async setPassPartout(card: Card) {
    let listEnvironmentCompatible: EnvironmentAccessControlMaintanance[] = [];
    await this._loaderService.presentLoaderWaiting();
    if (card.manufacturerSystem !== ManufacturerSystem.UNKNOWN) {
      //Search compatible manufacturer system
      for (var index in this.listEnvironmentAccessControlMaintanance) {
        if (
          this._enviromentService.manufacturerSystemsCompatible(
            card.manufacturerSystem,
            this.listEnvironmentAccessControlMaintanance[index]
              .manufacturerSystem
          )
        ) {
          listEnvironmentCompatible.push(
            this.listEnvironmentAccessControlMaintanance[index]
          );
        }
      }
    } else {
      await this.switchCardEnvironmentByIDCardAndIDEnvironment(
        card.id,
        this.listEnvironmentAccessControlMaintanance[0].id
      );
      for (var index in this.listEnvironmentAccessControlMaintanance) {
        if (
          this._enviromentService.manufacturerSystemsCompatible(
            this.listEnvironmentAccessControlMaintanance[0].manufacturerSystem,
            this.listEnvironmentAccessControlMaintanance[index]
              .manufacturerSystem
          )
        ) {
          listEnvironmentCompatible.push(
            this.listEnvironmentAccessControlMaintanance[index]
          );
        }
      }
    }

    for (var index in listEnvironmentCompatible) {
      if (
        !this.getCardEnvironmentByEnvironment(listEnvironmentCompatible[index])
      ) {
        await this.switchCardEnvironmentByIDCardAndIDEnvironment(
          card.id,
          listEnvironmentCompatible[index].id
        );
      }
    }

    await this._loaderService.dismissLoaderWaiting();
  }

  /**
   * Get card environment object by environment
   *
   * @param enviroment Environment to search card environment
   * @returns
   */
  public getCardEnvironmentByEnvironment(
    enviroment: EnvironmentAccessControlMaintanance
  ): CardEnvironment {
    return this.listCardEnvironment.find((cardEnvironment: CardEnvironment) => {
      return (
        cardEnvironment?.active &&
        cardEnvironment?.idEnvironment === enviroment.id
      );
    });
  }

  /**
   * Check if card has
   * @returns
   */
  public hasAtLeastOneEnvironments(): boolean {
    if (this.listCardEnvironment.length > 0) {
      return true;
    } else {
      return false;
    }
  }

  /**
   * Active/disactive card environment by id card and id environment
   *
   * @param idCard ID card to swtich card environment
   * @param idEnvironment ID environment to swtich card
   * @returns
   */
  public switchCardEnvironmentByIDCardAndIDEnvironment(
    idCard: number,
    idEnvironment: number
  ): Promise<CardEnvironment> {
    return this._http
      .post<CardEnvironment>(
        environment.httpServerRoot +
        AppSettings.API_URL_CARD +
        "/" +
        idCard +
        "/environment/" +
        idEnvironment +
        "/switch",
        {}
      )
      .pipe(
        tap(() => {
          this.propagateCardOperation(GenericOperationKind.CARD_EDIT, idCard);
        }),
        tap((cardEnvironment: CardEnvironment) =>
          this._store.dispatch(
            cardEnvironmentSwithcActiveSuccessAction({
              cardEnvironment,
            })
          )
        )
      )
      .toPromise();
  }

  public assignCalendarToCardEnvironmentByCardEnvironmentAndIDTimeSlot(
    cardEnvironment: CardEnvironment,
    idCalendar: number
  ): Promise<any> {
    const bodyRequest = {
      idCalendar: idCalendar,
    };
    return this._http
      .patch(
        environment.httpServerRoot +
        AppSettings.API_URL_CARD +
        "/environment/" +
        cardEnvironment.id +
        "/calendar",
        bodyRequest
      )
      .pipe(
        tap(() => {
          this.propagateCardOperation(
            GenericOperationKind.CARDENVIRONMENT_CALENDAR_CHANGE,
            cardEnvironment.id
          );
        }),
        tap(() => {
          this._store.dispatch(
            cardEnvironmentAssignCalendarAction({
              cardEnvironment,
              idCalendar,
            })
          );
        })
      )
      .toPromise();
  }
  /**
   * Active/disactive card environment by id card and id environment
   *
   * @param idCard ID card to swtich card environment
   * @param idEnvironment ID environment to swtich card
   * @returns
   */
  public async assignTimeSlotToCardEnvironmentByCardEnvironmentAndIDTimeSlot(
    cardEnvironment: CardEnvironment,
    idTimeSlot: number
  ): Promise<any> {
    const bodyRequest = {
      idTimeslot: idTimeSlot,
    };
    return this._http
      .patch(
        environment.httpServerRoot +
        AppSettings.API_URL_CARD +
        "/environment/" +
        cardEnvironment.id +
        "/timeslot1",
        bodyRequest
      )
      .pipe(
        tap(() => {
          this.propagateCardOperation(
            GenericOperationKind.CARDENVIRONMENT_TIMESLOT_CHANGE,
            cardEnvironment.id
          );
        }),
        tap(() => {
          this._store.dispatch(
            cardEnvironmentAssignTimeSlotAction({
              cardEnvironment,
              idTimeSlot,
            })
          );
        })
      )
      .toPromise();
  }

  /**
   * Propagate card operation over MQTT
   *
   * @param operation Generic operation kind
   * @param idCard ID card to propagate
   */
  private propagateCardOperation(
    operation: GenericOperationKind,
    idCard: number
  ): void {
    const mqttAddress =
      AppSettings.INSTANCE_GUID_VIRTUAL_VARIABLE +
      "_" +
      AppReserved.RESERVED_DB_CARD_DATAUPDATED;
    const mqttMessage = operation.toString() + "|" + idCard.toString();

    this._mqttClient.publishAddress(mqttAddress, mqttMessage);
  }

  /**
   * Get state of card by cardEffectiveState
   */
  public getStringStateByCardEffectiveState(cardEffectiveState) {
    let stringState = "";

    switch (cardEffectiveState) {
      case CardEffectiveState.ACTIVE:
        stringState = this._active;
        break;
      case CardEffectiveState.BLOCKED:
        stringState = this._locked;
        break;
      case CardEffectiveState.BUS_WRITTEN:
        stringState = this._awaitingActivation;
        break;
      case CardEffectiveState.EXPIRED:
        stringState = this._expired;
        break;
      case CardEffectiveState.VERGIN:
        stringState = this._new;
        break;
    }
    return stringState;
  }

  /**
   * Calculate card effective state by input parameter
   *
   * @param isValid card valid field
   * @param busWrittten card bus written field
   * @param state card state field expressed by cardState enum
   * @param expirationDate expiration date as string
   * @param expirationTime expiration time as string
   * @returns
   */
  public calculateCardEffectiveState(
    isValid: boolean,
    busWrittten: boolean,
    state: CardState,
    expirationDate: string,
    expirationTime: string
  ): CardEffectiveState {
    let cardEffectiveState = CardEffectiveState.EXPIRED;

    if (isValid) {
      switch (Number(state)) {
        case CardState.BLOCKED_NOT_REGISTERED:
          cardEffectiveState = CardEffectiveState.BLOCKED;
          break;
        case CardState.BLOCKED_REGISTERED:
          cardEffectiveState = CardEffectiveState.BLOCKED;
          break;
        case CardState.NOT_REGISTERED:
          cardEffectiveState = CardEffectiveState.VERGIN;
          break;
        case CardState.REGISTERED:
          const now = moment();
          if (
            moment().isSameOrBefore(
              moment(
                expirationDate + " " + expirationTime,
                "DD/MM/YYYY hh:mm:ss"
              )
            )
          ) {
            if (busWrittten) {
              cardEffectiveState = CardEffectiveState.ACTIVE;
            } else {
              cardEffectiveState = CardEffectiveState.BUS_WRITTEN;
            }
          } else {
            cardEffectiveState = CardEffectiveState.EXPIRED;
          }

          break;
      }
    }

    return cardEffectiveState;
  }
  /**
   * Load server settings from store
   */
  private loadServerSettings(): void {
    this._selectServerSettingsSubscription = this._store
      .select(serverSettings)
      .subscribe((serverSettings: Settings) => {
        this._serverSettings = serverSettings;
      });
  }

  //#region Unsubscribe from "_store.selec(...)" Observable

  /**
   * This method handles the unsunìbscription from the Observables returned by the "_store.select(...)" invocations.
   * It SHOULD BE CALLED EACH TIME THE DATA STREAMS ARE NOT NEEDED ANYMORE; this means in:
   *
   * * OnDestroy() [Components/Services]
   * * OnViewWillLeave() [Pages]
   */
  private unsubscribeStoreSelect(): void {
    this._selectServerSettingsSubscription.unsubscribe();
  }

  //#endregion

  /**
   * Load list of card environment
   */
  private loadListCardEnvironment(): void {
    this._listCardEnvironmentSubscription = this._store
      .select(listCardEnvironment)
      .pipe(
        filter(
          (listCardEnvironmentObject: GenericStoreApiData) =>
            !!listCardEnvironmentObject
        ),

        filter(
          (listCardEnvironmentObject: GenericStoreApiData) =>
            listCardEnvironmentObject.loaded
        ),

        filter(
          (listCardEnvironmentObject: GenericStoreApiData) =>
            !listCardEnvironmentObject.errorGeneric
        ),
        map(
          (listCardEnvironmentObject: GenericStoreApiData) =>
            listCardEnvironmentObject.data?.data || []
        )
      )
      .subscribe((listCardEnvironment: Array<CardEnvironment>) => {
        this.listCardEnvironment = listCardEnvironment;
      });
  }

  /**
   * Load list environment for access control
   */
  private loadListEnvironmentAccessControl(): void {
    this._listEnvironmentAccessControlSubscription = this._store
      .select(listEnvironmentAccessControlMaintanance)
      .pipe(
        filter(
          (listEnvironmentObject: GenericStoreApiData) =>
            !!listEnvironmentObject
        ),

        filter(
          (listEnvironmentObject: GenericStoreApiData) =>
            listEnvironmentObject.loaded
        ),
        filter(
          (listEnvironmentObject: GenericStoreApiData) =>
            !listEnvironmentObject.errorGeneric
        ),
        map(
          (listEnvironmentObject: GenericStoreApiData) =>
            listEnvironmentObject.data?.data || []
        ),
        map((listEnvironment: Array<EnvironmentAccessControlMaintanance>) => {
          return listEnvironment.filter(
            (environment: EnvironmentAccessControlMaintanance) =>
              environment.environmentType == EnvironmentType.COMMON_AREA ||
              environment.environmentType == EnvironmentType.ROOM ||
              environment.environmentType == EnvironmentType.CUSTOMIZED
          );
        }),
        map((listEnvironment: Array<EnvironmentAccessControlMaintanance>) => {
          return listEnvironment.filter(
            (environment: EnvironmentAccessControlMaintanance) =>
              this._enviromentService.isEnvironmentForAccessControl(environment)
          );
        })
      )
      .subscribe(
        (listEnvironment: Array<EnvironmentAccessControlMaintanance>) => {
          this.listEnvironmentAccessControlMaintanance = listEnvironment.sort(
            (
              env1: EnvironmentAccessControlMaintanance,
              env2: EnvironmentAccessControlMaintanance
            ) => {
              if (env1.environmentType == EnvironmentType.ROOM) {
                return -1;
              }
              return 1;
            }
          );
        }
      );
  }

  //#region Translation
  public loadTranslation(): void {
    this._translateService
      .get([
        "dictionary.locked",
        "dictionary.newFemale",
        "dictionary.activated",
        "dictionary.expired",
        "dictionary.awaitingActivation",
        "dictionary.UIDMandatory",
        "dictionary.attentionAnotherCardWithSameUID",
        "dictionary.errorUID8char",
        "dictionary.needToDeleteCard",
        "dictionary.environmentMandatory",
      ])
      .subscribe((translation) => {
        this._locked = translation["dictionary.locked"];
        this._new = translation["dictionary.newFemale"];
        this._active = translation["dictionary.activated"];
        this._expired = translation["dictionary.expired"];
        this._awaitingActivation = translation["dictionary.awaitingActivation"];
        this._lblUIDMandatory = translation["dictionary.UIDMandatory"];
        this._lblAnotherCardWithSameUID =
          translation["dictionary.attentionAnotherCardWithSameUID"];
        this._lblUID8Char = translation["dictionary.errorUID8char"];
        this._lblNeedToDeleteCard = translation["dictionary.needToDeleteCard"];
        this._lblEnvironmentMandatory =
          translation["dictionary.environmentMandatory"];
      });
  }

  public subscribeTranslationEvent() {
    this._translateSubscription = this._translateService.onLangChange.subscribe(
      () => {
        this.loadTranslation();
      }
    );
  }
  //#endregion
}
